interface ErrorWithMessage {
  message: string;
  stack?: string;
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

export function reportError(error: unknown, context?: Record<string, any>) {
  const errorMessage = getErrorMessage(error);

  if (process.env.NODE_ENV !== 'production') {
    console.error('Error:', errorMessage);
    if (context) {
      console.error('Context:', context);
    }
  }
}

export function withErrorReporting<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: Record<string, any>
) {
  return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try {
      return await fn(...args);
    } catch (error) {
      reportError(error, {
        ...context,
        functionName: fn.name,
        arguments: args,
      });
      throw error;
    }
  };
}

export function reportTokenDebug(message: string, data: Record<string, any>) {
  if (process.env.NODE_ENV !== 'production') {
    console.debug(message, data);
  }
}

export function reportTokenSuccess(message: string, data: Record<string, any>) {
  if (process.env.NODE_ENV !== 'production') {
    console.info(`Token Success: ${message}`, data);
  }
}

export function reportTokenError(message: string, data: Record<string, any>) {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`Token Error: ${message}`, data);
  }
}

export function reportTokenWarning(message: string, data: Record<string, any>) {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Token Warning: ${message}`, data);
  }
}
